import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getDramasByCategory, dramas } from '@/mocks/dramas';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  const currentCategory = categories.find(c => c.slug === activeSlug) || categories[0];
  const filteredDramas = useMemo(() => getDramasByCategory(activeSlug), [activeSlug]);

  const sortedDramas = useMemo(() => {
    const list = [...filteredDramas];
    if (sortBy === 'rating') return list.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'year') return list.sort((a, b) => b.year - a.year);
    return list;
  }, [filteredDramas, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.REACT_APP_NAVIGATE(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name === '全部' ? '剧集大全' : currentCategory.name },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `星空影院 - ${currentCategory.name}`,
    description: `星空影院${currentCategory.name}分类，聚合热播电视剧、高分剧集全集在线观看，免费高清追剧。`,
    url: `https://www.xingkongyingyuan.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '星空影院',
      url: 'https://www.xingkongyingyuan.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {currentCategory.name === '全部' ? '剧集大全' : currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-600 mt-2">
              共 {filteredDramas.length} 部剧集 · 星空影院聚合热播电视剧高分剧集全集在线观看
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-500">排序：</span>
            {[
              { value: 'default', label: '默认' },
              { value: 'rating', label: '评分最高' },
              { value: 'year', label: '最新上线' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value as typeof sortBy)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
                  sortBy === opt.value
                    ? 'bg-accent-500/20 text-accent-500'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {sortedDramas.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-movie-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无相关剧集</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
              {sortedDramas.map((drama) => (
                <Link
                  key={drama.id}
                  to={`/article/${drama.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                    <img
                      src={drama.posterUrl}
                      alt={drama.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-accent-500 text-white text-xs font-semibold rounded-md">
                        {drama.rating}
                      </span>
                    </div>
                    {drama.isNew && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-md">
                          NEW
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <p className="text-white text-xs leading-relaxed line-clamp-3">{drama.description}</p>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {drama.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">
                    {drama.category} · {drama.episodes}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}